import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

/**
 * Badge — small pill-style label for tags, statuses, etc.
 *
 * Props:
 * - children: string or JSX (the text or icon inside)
 * - variant: visual theme ('primary', 'success', 'danger', 'warning', 'info', 'gray')
 * - size: badge size ('sm' | 'md' | 'lg')
 * - className: extra custom classes
 * - rounded: full or md (default = full)
 */

export const Badge = ({ children, variant = 'gray', size = 'md', rounded = 'full', className }) => {
  const variants = {
    primary: 'bg-indigo-100 text-indigo-700 border border-indigo-200',
    success: 'bg-green-100 text-green-700 border border-green-200',
    danger: 'bg-red-100 text-red-700 border border-red-200',
    warning: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    info: 'bg-blue-100 text-blue-700 border border-blue-200',
    gray: 'bg-gray-100 text-gray-700 border border-gray-200',
  };

  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center font-medium select-none',
        variants[variant],
        sizes[size],
        rounded === 'full' ? 'rounded-full' : 'rounded-md',
        className
      )}
    >
      {children}
    </span>
  );
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'success', 'danger', 'warning', 'info', 'gray']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  rounded: PropTypes.oneOf(['full', 'md']),
  className: PropTypes.string,
};
